import { forwardRef, useId, type ComponentProps, type CSSProperties, type ReactNode } from 'react';
import type { TextareaProps } from '@mantine/core';
import { useTheme } from '@contexts/ThemeContext';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  getJarvisControlStyle,
  jarvisControlClassName,
  renderFieldDescription,
  renderFieldError,
  renderFieldLabel,
  type JarvisFieldSize,
} from './jarvis-field-utils';

export type JarvisTextareaProps = TextareaProps;

/** Mantine layout / style-system keys that must not reach the DOM. */
const MANTINE_LAYOUT_KEYS = new Set([
  'm',
  'mx',
  'my',
  'mt',
  'mb',
  'ml',
  'mr',
  'p',
  'px',
  'py',
  'pt',
  'pb',
  'pl',
  'pr',
  'w',
  'miw',
  'maw',
  'h',
  'mih',
  'mah',
  'flex',
  'display',
  'bg',
  'c',
  'ff',
  'fz',
  'fw',
  'lts',
  'ta',
  'lh',
  'tt',
  'td',
  'hiddenFrom',
  'visibleFrom',
  'lightHidden',
  'darkHidden',
]);

/**
 * JarvisTextarea - Themed textarea wrapping shadcn Textarea.
 * Keeps the previous Mantine Textarea-compatible props API (label, error,
 * description, size, styles.input, rows/minRows, etc.) so call sites
 * do not need to change.
 */
export const JarvisTextarea = forwardRef<HTMLTextAreaElement, JarvisTextareaProps>((props, ref) => {
  const { colors: themeColors } = useTheme();
  const autoId = useId();

  const {
    label,
    description,
    error,
    size = 'sm',
    styles,
    className,
    style,
    required,
    withAsterisk,
    disabled,
    id,
    name,
    rows,
    minRows,
    maxRows: _maxRows,
    autosize: _autosize,
    // Mantine-only / non-DOM props — strip so they never hit <textarea>
    radius: _radius,
    variant: _variant,
    classNames: _classNames,
    unstyled: _unstyled,
    wrapperProps: _wrapperProps,
    inputWrapperOrder: _inputWrapperOrder,
    inputContainer: _inputContainer,
    labelProps: _labelProps,
    descriptionProps: _descriptionProps,
    errorProps: _errorProps,
    leftSection: _leftSection,
    rightSection: _rightSection,
    leftSectionWidth: _leftSectionWidth,
    rightSectionWidth: _rightSectionWidth,
    leftSectionPointerEvents: _leftSectionPointerEvents,
    rightSectionPointerEvents: _rightSectionPointerEvents,
    withErrorStyles: _withErrorStyles,
    pointer: _pointer,
    __staticSelector: _staticSelector,
    mod: _mod,
    vars: _vars,
    attributes: _attributes,
    resize: mantineResize,
    ...rest
  } = props as TextareaProps & Record<string, unknown>;

  const textareaProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (MANTINE_LAYOUT_KEYS.has(key) || key.startsWith('__')) continue;
    textareaProps[key] = value;
  }

  const fieldId = (id as string | undefined) ?? autoId;
  const hasError = Boolean(error);
  const resolvedRows = (rows as number | undefined) ?? (minRows as number | undefined) ?? undefined;

  const controlStyle = getJarvisControlStyle(themeColors, {
    hasError,
    styles,
    style: undefined,
  });

  // Textarea size map reuses height utilities poorly — drop fixed h-* for multiline.
  const sizeClass = jarvisControlClassName(size as JarvisFieldSize).replace(/\bh-\d+\b/g, 'min-h-[80px]');

  const resizeStyle: CSSProperties | undefined =
    mantineResize == null
      ? undefined
      : mantineResize === 'none'
        ? { resize: 'none' }
        : typeof mantineResize === 'string'
          ? ({ resize: mantineResize } as CSSProperties)
          : undefined;

  return (
    <div className="w-full" style={style as CSSProperties | undefined}>
      {renderFieldLabel({
        id: fieldId,
        label: label as ReactNode,
        required: required as boolean | undefined,
        withAsterisk: withAsterisk as boolean | undefined,
        textColor: themeColors.text,
      })}

      <Textarea
        {...(textareaProps as ComponentProps<typeof Textarea>)}
        ref={ref}
        id={fieldId}
        name={name as string | undefined}
        disabled={disabled as boolean | undefined}
        required={required as boolean | undefined}
        rows={resolvedRows}
        aria-invalid={hasError || undefined}
        className={cn(
          sizeClass,
          'h-auto py-2',
          hasError && 'border-destructive focus-visible:ring-destructive',
          className as string | undefined
        )}
        style={{ ...controlStyle, ...resizeStyle }}
      />

      {renderFieldDescription(description as ReactNode, themeColors.textSecondary)}
      {renderFieldError(error as ReactNode, themeColors.error)}
    </div>
  );
});

JarvisTextarea.displayName = 'JarvisTextarea';

export default JarvisTextarea;
